import React, {
  useEffect, useState, useRef, useMemo,
} from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classes from '../TopMedications/medication.module.scss';
import { RangeDatePicker } from '../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import FilterButton from '../../../_common/FilterButton/FilterButton';
import Select from '../../../_common/Form/Selects/Select/Select';
import { specOptions } from '../../../../utils/data/data';
import InputField from '../../../_common/Form/TextFields/InputField';
import Button from '../../../_common/Form/Buttons/Button';
import utils from '../../../../utils/utils';
import { useQuery } from '../../../../utils/hooks/useQuery';
import { getTopCities } from '../../../../store/actions/dashboard';
import { getSelectRepListRequest } from '../../../../store/actions/users';
import Loader from '../../../_common/Loader/Loader';
import TableAndPagination from '../../../_common/TableAndPagination/TableAndPagination';
import accountHelpers from '../../../../utils/accountHelpers';

const tableHealthHeader = [
  {
    name: 'Name',
    path: 'name',
  },
  {
    name: 'Quantity',
    path: 'quantity',
  },
  {
    name: 'Patients',
    path: 'patients',
  },
  {
    name: 'Physicians',
    path: 'physicians',
  },
  {
    name: 'Prescription Count',
    path: 'sales',
  },
  {
    name: 'Total Sales',
    path: 'revenue',
    type: 'currency',
  },
];

const typeData = [
  {
    title: 'RX',
    key: 'rxKey',
    value: 'rx',
  },
  {
    title: 'OTC',
    key: 'otcKey',
    value: 'otc',
  },
];

const specialData = [
  {
    title: 'SortPak',
    key: 'salesCode',
    value: 'M',
  },
];

const collectRequestData = (requestData) => {
  const data = utils.deleteEmptyKeys(requestData);

  if (data.start) {
    if (!data.end) {
      data.end = moment(data.start).format('YYYY-MM-DD');
    } else {
      data.end = moment(data.end).format('YYYY-MM-DD');
    }
  }

  return data;
};

const thisMonthStartDay = moment().startOf('month').format('YYYY-MM-DD');
const thisMonthEndDate = moment().format('YYYY-MM-DD');
const limitRep = 20;

const TopCities = () => {
  const isHealth = accountHelpers.isInOneOfRoles(['Health']);
  const isManufacturerUsers = accountHelpers.isInRole('Manufacturer Users');

  const { query, setQuery, queryToStringify } = useQuery();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { dashboardTotalPage, dashboardPageData } = useSelector((store) => store.dashboard);
  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);

  const firstLoad = useRef(true);

  const [requestData, setRequestData] = useState({
    start: query.start || thisMonthStartDay,
    end: query.start && !query.end ? '' : query.end || thisMonthEndDate,
    specialization: query.specialization || '',
    product: query.product || '',
    zip: query.zip || '',
    rep: query.rep || '',
    page: query.page || 1,
    limit: query.limit || 15,
  });

  const [loading, setLoading] = useState(false);
  const {
    page, limit, specialization, zip, rep, start, end, product,
  } = requestData;

  useEffect(() => {
    if (firstLoad.current) {
      dispatch(getSelectRepListRequest({ limit: limitRep, role: 'Rep' }));
    }

    (async () => {
      setLoading(true);
      await dispatch(getTopCities(collectRequestData(requestData)));
      setLoading(false);
      firstLoad.current = false;
    })();
  }, []);

  const onChangeDate = (currentDate) => {
    setRequestData((prev) => ({ ...prev, start: currentDate[0], end: currentDate[1] }));
  };

  const nextOptionListRequest = async (repPage) => {
    await dispatch(getSelectRepListRequest({ page: repPage, limit: limitRep, role: 'Rep' }));
  };

  const changeZip = (value) => {
    if (!Number.isNaN(+value) || value === '') {
      setRequestData((prev) => ({ ...prev, zip: value }));
    }
  };

  const changeRequestData = (path, value) => {
    setRequestData((prev) => ({ ...prev, [path]: value }));
  };

  const clearFilters = async () => {
    const defaultRequestData = {
      start: thisMonthStartDay,
      end: thisMonthEndDate,
      page: 1,
      limit: 15,
    };

    setRequestData(defaultRequestData);

    setQuery(defaultRequestData);

    setLoading(true);
    await dispatch(getTopCities(defaultRequestData));
    setLoading(false);
  };

  const onChangePageData = async (path, count) => {
    const newRequestData = { ...requestData, [path]: count };

    if (path === 'limit') {
      newRequestData.page = 1;
    }

    const data = collectRequestData(newRequestData);
    await dispatch(getTopCities(data));

    setRequestData(newRequestData);

    setQuery(data);
  };

  const submitFilters = async (e) => {
    e.preventDefault();

    const data = collectRequestData(requestData);
    data.page = 1;

    setLoading(true);
    await dispatch(getTopCities(data));
    setRequestData((prev) => ({ ...prev, page: 1 }));

    setQuery(data);
    setLoading(false);
  };

  const singleRepPageClick = (name) => {
    if (accountHelpers.getUser().role === 'Admin' || accountHelpers.getUser().role === 'Health') {
      const queryRequest = queryToStringify({
        specialization,
        zip,
        rep,
        start,
        end,
        product,
        name,
        report: 'City',
      });

      navigate(`/dashboard/report-single${queryRequest}`);
    }
  };

  const tableHeader = useMemo(() => {
    const header = [
      {
        name: 'Name',
        path: 'name',
      },
      {
        name: 'Patients',
        path: 'patients',
      },
      {
        name: 'Physicians',
        path: 'physicians',
      },
      {
        name: 'Prescription Count',
        path: 'sales',
      },
      {
        name: 'Revenue per sale',
        path: 'revenuePerSale',
        type: 'currency',
      },
      {
        name: 'Total Sales',
        path: 'revenue',
        type: 'currency',
      },
      {
        name: 'Cost per sale',
        path: 'costPerSale',
        type: 'currency',
      },
      {
        name: 'Cost',
        path: 'cost',
        type: 'currency',
      },
      {
        name: 'Profit',
        path: 'profit',
        type: 'currency',
      },
      {
        name: 'Margin',
        path: 'margin',
      },
    ];

    if (isHealth) {
      return tableHealthHeader;
    }

    if (isManufacturerUsers) {
      return header.filter((h) => h.path !== 'profit' && h.path !== 'margin');
    }

    return header;
  }, [isHealth, isManufacturerUsers]);

  return (
    <div className={classes.wrapper}>
      <form onSubmit={submitFilters} className={classes.wrapper_header}>
        <div className={classes.first_box_container}>
          <div style={{ marginRight: 10 }}>
            <RangeDatePicker
              currentDate={requestData}
              onChangeDate={onChangeDate}
              showLastYear
            />
          </div>

          <div className={classes.filter_button}>
            <FilterButton activeValue="rx" buttonsData={typeData} onClick={() => {}} />
          </div>

          <div className={classes.filter_button_sort_pak}>
            <FilterButton activeValue="M" buttonsData={specialData} onClick={() => {}} />
          </div>
        </div>

        <div className={classes.second_box_container}>

          {!isHealth && (
          <div className={classes.select_rep}>
            <Select
              label="All reps"
              nextOptionListRequest={nextOptionListRequest}
              options={selectRepList}
              totalPage={pageCount}
              currentPage={currentPage}
              limit={limitRep}
              currentOption={rep}
              onChange={(option) => changeRequestData('rep', option)}
              optionPathname="name"
            />
          </div>
          )}

          {!isHealth && (
          <div className={classes.specialization}>
            <Select
              label="All specializations"
              options={specOptions}
              currentOption={specialization}
              onChange={(option) => changeRequestData('specialization', option)}
            />
          </div>
          )}

          <div className={classes.product}>
            <InputField
              label="Product"
              onChange={(value) => changeRequestData('product', value)}
              value={product}
            />
          </div>

          <div className={classes.product}>
            <InputField
              label="Zip code"
              value={zip}
              onChange={changeZip}
            />
          </div>

          <div className={classes.buttonContainer}>
            <Button
              className={classes.updateButton}
              title="Update"
              type="submit"
            />

            <Button
              className={classes.updateButton}
              title="Clear"
              clearButton
              onClick={clearFilters}
            />
          </div>
        </div>
      </form>
      <div className={classes.table_wrapper}>
        {loading
          ? <Loader />

          : (
            <TableAndPagination
              onClick={singleRepPageClick}
              onChange={onChangePageData}
              data={dashboardPageData}
              header={tableHeader}
              totalPage={+dashboardTotalPage}
              page={+page - 1}
              limit={+limit}
            />
          )}
      </div>
    </div>
  );
};

export default TopCities;
